import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Store, HeartHandshake, Flame } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const SAGE = "https://images.pexels.com/photos/3020635/pexels-photo-3020635.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [role, setRole] = useState(params.get("role") === "aid_organization" ? "aid_organization" : "grocery_store");
  const [form, setForm] = useState({ org_name: "", name: "", email: "", password: "", phone: "", city: "", zip_code: "", address: "" });
  const [warming, setWarming] = useState(false);
  const [servicesStr, setServicesStr] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = { ...form, role };
      if (role === "aid_organization") {
        payload.is_warming_shelter = warming;
        payload.services = servicesStr.split(",").map((s) => s.trim()).filter(Boolean);
      }
      const u = await register(payload);
      navigate(u.role === "grocery_store" ? "/store" : "/organization");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const roles = [
    { key: "grocery_store", label: "Grocery Store", icon: Store, desc: "Post surplus food" },
    { key: "aid_organization", label: "Aid Organization", icon: HeartHandshake, desc: "Collect & redistribute" },
  ];

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={SAGE} alt="Sage leaves" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-emerald-900/70" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <Link to="/" className="flex items-center gap-2">
            <Logo textClass="text-white" />
          </Link>
          <div>
            <h2 className="font-heading text-4xl font-semibold tracking-tight leading-tight">Join the food<br />rescue network.</h2>
            <p className="text-emerald-100/80 mt-4 max-w-sm">Every account added means more food saved and more people fed.</p>
          </div>
          <span />
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-stone-50 overflow-y-auto">
        <div className="w-full max-w-md py-6">
          <Link to="/" className="flex items-center gap-2 lg:hidden mb-8">
            <Logo />
          </Link>
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-stone-900">Create account</h1>
          <p className="text-stone-500 mt-2">Choose your role to get started.</p>

          <div className="grid grid-cols-2 gap-3 mt-6">
            {roles.map((r) => (
              <button
                key={r.key}
                type="button"
                data-testid={`role-${r.key}`}
                onClick={() => setRole(r.key)}
                className={`text-left rounded-2xl border p-4 transition-all ${role === r.key ? "border-emerald-800 bg-emerald-50 ring-2 ring-emerald-800/20" : "border-stone-200 bg-white hover:border-stone-300"}`}
              >
                <r.icon className={`w-5 h-5 ${role === r.key ? "text-emerald-800" : "text-stone-500"}`} />
                <p className="font-medium text-stone-900 mt-2 text-sm">{r.label}</p>
                <p className="text-xs text-stone-500">{r.desc}</p>
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <Field label={role === "grocery_store" ? "Store name" : "Organization name"} testid="reg-orgname" value={form.org_name} onChange={set("org_name")} required />
            <Field label="Contact name" testid="reg-name" value={form.name} onChange={set("name")} required />
            <div className="grid grid-cols-2 gap-4">
              <Field label="Email" type="email" testid="reg-email" value={form.email} onChange={set("email")} required />
              <Field label="Password" type="password" testid="reg-password" value={form.password} onChange={set("password")} required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="City" testid="reg-city" value={form.city} onChange={set("city")} required />
              <Field label="ZIP / Postal" testid="reg-zip" value={form.zip_code} onChange={set("zip_code")} required />
            </div>
            <Field label="Phone (for SMS alerts)" testid="reg-phone" value={form.phone} onChange={set("phone")} placeholder="+14155552671" />
            <Field label="Address" testid="reg-address" value={form.address} onChange={set("address")} placeholder="Pickup / office address" />

            {role === "aid_organization" && (
              <div className="space-y-4 rounded-2xl bg-stone-50 border border-stone-200 p-4">
                <p className="text-xs tracking-widest uppercase text-stone-500">Shelter profile (shown on the public map)</p>
                <div className="space-y-2">
                  <Label className="text-xs tracking-widest uppercase text-stone-500">Services offered (comma separated)</Label>
                  <Input data-testid="reg-services" value={servicesStr} onChange={(e) => setServicesStr(e.target.value)} placeholder="Meals, Showers, Beds, Clothing" className="rounded-xl border-stone-200 bg-white h-11" />
                </div>
                <div className="flex items-center gap-3">
                  <Switch data-testid="reg-warming" checked={warming} onCheckedChange={setWarming} />
                  <span className="flex items-center gap-1.5 text-sm text-stone-700"><Flame className="w-4 h-4 text-orange-600" /> We are a warming shelter</span>
                </div>
              </div>
            )}

            {error && <p data-testid="register-error" className="text-sm text-red-600">{error}</p>}
            <Button data-testid="register-submit" type="submit" disabled={loading} className="w-full rounded-full bg-emerald-800 hover:bg-emerald-900 h-11 text-white">
              {loading ? "Creating…" : "Create account"}
            </Button>
          </form>
          <p className="text-sm text-stone-500 mt-6">
            Already have an account?{" "}
            <Link to="/login" className="text-emerald-800 font-medium hover:underline">Log in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, type = "text", ...props }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs tracking-widest uppercase text-stone-500">{label}</Label>
      <Input data-testid={testid} type={type} className="rounded-xl border-stone-200 bg-white h-11" {...props} />
    </div>
  );
}
